import React, { useState, useEffect, useMemo } from 'react';
import { Card, Badge, Button, Spinner, Alert, Table } from '@/components/ui';
import { Form } from '../../../services/formService';
import type { Task } from '../../../types/Task';
import SectionCard from './FormViews/@components/SectionCard';
import ServerPagination from '../../../components/ServerPagination';
import { getAssetForTaskGroup } from '../../../utils/assetExtraction';
import { getFormEmployeeDisplayName } from '../utils/formDisplayName';
import { FormValidationResult, TASK_ZERO_LABOUR_HOURS_CODE } from '../utils/formValidation';
import {
  buildMechanicEffortSummaries,
  buildMechanicSubmissionLabourLineKey,
  formatMechanicMileageDisplay,
  getMechanicVehicleUnitForWorkCompletedGroup,
  getMechanicWorkCompletedRows,
  isMechanicDailyLog,
  isMechanicDefectMarkedCompleted,
  MECHANIC_SUBMISSION_LABOUR_LINE_KEY_FALLBACK,
  type MechanicWorkCompletedRow,
} from '../utils/mechanicDailyLog';
import {
  aggregateFormLabourOtBreakdownDisplay,
  resolveLabourLineCostForFormTask,
  type FormLabourOvertimeAllocationRow,
  type PersistedLabourEffort,
} from '../../../utils/labourOvertimeDisplay';
import { formUsesHeLineItemSummary, parseHeGridRows } from '../../hired-equipment/utils/heSummaryRows';
import HiredEquipmentSummaryTable from '../../hired-equipment/components/HiredEquipmentSummaryTable';
import HiredEquipmentTaskPictures from '../../hired-equipment/components/HiredEquipmentTaskPictures';
import HiredEquipmentInternalComment from '../../hired-equipment/components/HiredEquipmentInternalComment';
import { getHiredEquipmentInternalComment } from '../../hired-equipment/utils/internalComment';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { DAILY_LOG_CARD_LAYOUT_QUERY } from '../utils/dailyLogLayout';
import { formatDate } from '@/utils/displayDateTime';

// Helper function to safely parse float values
const safeParseFloat = (value: string | undefined, defaultValue: number = 0): number => {
  if (!value) return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

interface EffortSummary {
  type: string;
  description: string;
  quantity: number;
  rate: number;
  totalCost: number;
  unitOfMeasure: string;
  task: string;
  taskName?: string;
  asset?: string;
  assetNumber?: string;
  homeForemanAreaId?: number;
  taskId: number;
  comments?: string;
  /**
   * Primary activity quantity for this work-completed line (sum of QuantityPrimaryActiv
   * in the line). Shared by every effort on that line. Not keyed by task id, because
   * several lines can point at the same standing task.
   */
  accomplishment?: number;
  /** Labour: persisted DRM OT hours display; "—" when not on server effort. */
  otBreakdown?: string;
  excludeFromQuantityTotal?: boolean; // Flag to exclude from quantity totals (e.g., attachments, fuel surcharge)
}

interface TaskEffort {
  taskId: string;
  efforts: EffortSummary[];
  totalCost: number;
}

// Accomplishment table: one row per task group with optional fields (hide columns with no data)
interface AccomplishmentRow {
  taskGroupId: string;
  taskId: number;
  taskDisplay: string;
  taskName: string;
  assetDisplay: string;
  accomplishment?: number;
  unitOfMeasure?: string;
  numberPasses?: string;
  numberPasses1?: string;
  didCompleteEntireRoad?: string;
  didCompleteEntireR1?: string;
  isCHRISReport?: string;
  quantitySecondary?: string;
  didPerformSecondary1?: string;
  passesXLength?: string;
  plannedAccomplishment?: string;
}

type AccomplishmentAggregate = 'sum' | 'first';

interface AccomplishmentColumnConfig {
  fieldNames: string[]; // form field names to look for (e.g. ['UnitsMeasurementPrim', 'UnitsMeasurement'])
  rowKey: keyof Omit<AccomplishmentRow, 'taskGroupId' | 'taskId' | 'taskDisplay' | 'taskName' | 'assetDisplay'>;
  label: string;
  aggregate: AccomplishmentAggregate;
}

const ACCOMPLISHMENT_COLUMNS: AccomplishmentColumnConfig[] = [
  { fieldNames: ['QuantityPrimaryActiv'], rowKey: 'accomplishment', label: 'Accomplishment', aggregate: 'sum' },
  { fieldNames: ['UnitsMeasurementPrim', 'UnitsMeasurement'], rowKey: 'unitOfMeasure', label: 'Unit', aggregate: 'first' },
  { fieldNames: ['NumberPasses'], rowKey: 'numberPasses', label: 'Number of passes', aggregate: 'first' },
  { fieldNames: ['NumberPasses 1'], rowKey: 'numberPasses1', label: 'Number of passes (secondary)', aggregate: 'first' },
  { fieldNames: ['DidCompleteEntireRoa'], rowKey: 'didCompleteEntireRoad', label: 'Complete entire road?', aggregate: 'first' },
  { fieldNames: ['DidCompleteEntireR 1'], rowKey: 'didCompleteEntireR1', label: 'Complete entire road? (secondary)', aggregate: 'first' },
  { fieldNames: ['IsCHRISReport'], rowKey: 'isCHRISReport', label: 'CHRIS reporting', aggregate: 'first' },
  { fieldNames: ['QuantitySecondaryAct'], rowKey: 'quantitySecondary', label: 'Secondary quantity', aggregate: 'first' },
  { fieldNames: ['DidPerformSecondar 1'], rowKey: 'didPerformSecondary1', label: 'Did perform secondary', aggregate: 'first' },
  { fieldNames: ['PassesXLength'], rowKey: 'passesXLength', label: 'Passes × length', aggregate: 'first' },
  { fieldNames: ['PlannedAccomplishmen'], rowKey: 'plannedAccomplishment', label: 'Planned accomplishment', aggregate: 'first' },
];

interface FormSummaryViewProps {
  form: Form;
  isExpanded: boolean;
  onExpandToggle: (formId: string, e: React.MouseEvent) => void;
  availableTasks: Task[];
  tasksLoading: boolean;
  validationResult?: FormValidationResult;
  /** Labour efforts from API (clone tasks); merges DRM OT into summary. */
  persistedLabourEfforts?: PersistedLabourEffort[];
  labourOvertimeAllocations?: FormLabourOvertimeAllocationRow[];
  /** Refetch persisted labour + OT allocations after bucket save (e.g. from FormList). */
  onLabourOtAllocationsRefresh?: () => void | Promise<void>;
}

const truncateText = (text: string | undefined, maxLength: number = 100): string => {
  if (!text) return '-';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

// Resolve task id, name, display number, and asset for a task group (for Accomplishment table)
function getTaskAndAssetForGroup(
  taskGroupId: string,
  fields: Form['fields'],
  form: Form,
  availableTasks: Task[]
): { taskId: number; taskName: string; taskDisplay: string; assetDisplay: string } {
  const taskIdField = fields.find(f => f.field === 'TaskId' || f.path.match(/\.TaskId$/));
  const taskField = fields.find(f => f.field === 'Task' && f.path.endsWith('.Task'));
  const workCompletedId = fields.find(f => f.field === 'WorkCompletedID')?.value;
  const rawTaskId = taskIdField?.value || taskField?.value || workCompletedId || '0';
  const taskNumber = parseInt(rawTaskId, 10) || 0;
  let taskName = fields.find(f => f.field === 'TaskName')?.value || '';
  let taskNumberDisplay = taskNumber.toString();
  let assetInfo = '';
  let assetNumber = '';

  if (taskNumber) {
    const task = availableTasks.find(t => t.id === taskNumber);
    if (task) {
      taskName = task.name || task.description || taskName;
      taskNumberDisplay = task.taskNumber || taskNumber.toString();
      const assetFromFields = getAssetForTaskGroup(taskGroupId, form.fields);
      if (assetFromFields) {
        assetInfo = assetFromFields.name;
        assetNumber = assetFromFields.assetNumber;
      } else if (task.taskLocations?.[0]?.asset) {
        assetInfo = task.taskLocations[0].asset.name;
        assetNumber = task.taskLocations[0].asset.assetNumber || '';
      }
    }
  }
  if (!taskName) {
    taskName = taskField?.value || `Task ${taskGroupId}`;
  }

  let assetDisplay = '-';
  if (assetInfo || assetNumber) {
    let clean = assetInfo || '';
    if (clean && assetNumber && clean !== assetNumber && !clean.includes(`(${assetNumber})`) && !clean.includes(assetNumber)) {
      assetDisplay = `${clean} (${assetNumber})`;
    } else {
      assetDisplay = clean || assetNumber;
    }
  }
  return { taskId: taskNumber, taskName, taskDisplay: taskNumberDisplay, assetDisplay };
}


const FormSummaryView: React.FC<FormSummaryViewProps> = ({
  form,
  isExpanded,
  onExpandToggle,
  availableTasks,
  tasksLoading,
  validationResult,
  persistedLabourEfforts = [],
  labourOvertimeAllocations = [],
  onLabourOtAllocationsRefresh,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Card layout per entry on tablet and phone; the desktop table is unchanged (W-iP5).
  const cardLayout = useMediaQuery(DAILY_LOG_CARD_LAYOUT_QUERY);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };
  /** Mechanic summary: collapsed = unit header only; expanded = full defect list for that unit. */
  const [mechanicDefectUnitExpanded, setMechanicDefectUnitExpanded] = useState<Record<string, boolean>>({});

  const mechanicDefectsFixedByUnit = useMemo(() => {
    const isHiredEquipmentForm = form.fields.some(
      (field) =>
        field.path.includes('EquipmentTimeWorkedJ') ||
        field.field === 'AmountBilled' ||
        field.path.includes('SubcontractorNameCom')
    );
    const isMechanic = !isHiredEquipmentForm && isMechanicDailyLog(form);
    if (!isMechanic) {
      return [] as {
        groupId: string;
        unitTitle: string;
        workContext: string;
        defects: MechanicWorkCompletedRow['defects'];
      }[];
    }
    const rows = getMechanicWorkCompletedRows(form);
    const groupedByUnit = new Map<
      string,
      {
        groupId: string;
        unitTitle: string;
        workContexts: Set<string>;
        defects: MechanicWorkCompletedRow['defects'];
      }
    >();
    rows.forEach((row) => {
      const fixed = row.defects.filter((d) => isMechanicDefectMarkedCompleted(d.completed));
      if (fixed.length === 0) return;
      const workContext =
        [row.activity, row.activityType].filter(Boolean).join(' · ') ||
        `Work completed ${row.groupId}`;
      const unitTitle = row.unitLabel?.trim() || workContext;
      const unitKey = unitTitle.toLowerCase();
      const existing = groupedByUnit.get(unitKey);
      if (!existing) {
        groupedByUnit.set(unitKey, {
          groupId: row.groupId,
          unitTitle,
          workContexts: new Set([workContext]),
          defects: [...fixed],
        });
        return;
      }
      existing.workContexts.add(workContext);
      existing.defects.push(...fixed);
    });
    return Array.from(groupedByUnit.values()).map((entry) => ({
      groupId: entry.groupId,
      unitTitle: entry.unitTitle,
      workContext: Array.from(entry.workContexts).join(' | '),
      defects: entry.defects,
    }));
  }, [form]);

  // Add early return for loading state - don't render form content until tasks are loaded
  if (tasksLoading) {
    return (
      <Card>
        <Card.Header>
          <div className="tw-flex tw-justify-between tw-items-center">
            <div>
              <h5 className="tw-mb-0">{form.name}</h5>
              <small className="tw-text-content-muted tw-block">
                Loading task information...
              </small>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <Badge bg="secondary">
                {form.status || 'pending'}
              </Badge>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={(e) => onExpandToggle(form.id, e)}
              >
                <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="tw-flex tw-justify-center tw-items-center tw-py-4">
            <div className="tw-text-center">
              <Spinner className="tw-text-primary tw-mb-2">Loading...</Spinner>
              <p className="tw-text-content-muted tw-text-sm tw-mb-0">Loading task information...</p>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (formUsesHeLineItemSummary(form.fields)) {
    return (
      <div className="tw-max-h-[70vh] tw-overflow-y-auto">
        {validationResult?.hasErrors && (
          <Alert variant="danger" className="tw-mb-3">
            <strong>Cannot approve until errors are resolved</strong>
            <ul className="tw-mb-0 tw-mt-2 tw-pl-4">
              {validationResult.errors.map((error, index) => (
                <li key={index}>{error.message}</li>
              ))}
            </ul>
          </Alert>
        )}
        {validationResult?.hasWarnings && (
          <Alert variant="warning" className="tw-mb-3">
            <strong>Warnings</strong>
            <ul className="tw-mb-0 tw-mt-2 tw-pl-4">
              {validationResult.warnings.map((warning, index) => (
                <li key={index}>{warning.message}</li>
              ))}
            </ul>
          </Alert>
        )}
        <HiredEquipmentSummaryTable fields={form.fields} availableTasks={availableTasks} formId={form.id} />
        <HiredEquipmentInternalComment
          value={getHiredEquipmentInternalComment(form.fields)}
          className="tw-mt-3"
        />
        <HiredEquipmentTaskPictures
          formId={form.id}
          rows={parseHeGridRows(form.fields, availableTasks)}
        />
      </div>
    );
  }

  // Get foreman area and date
  const defaultForemanArea = form.fields.find(f => f.field === 'ForemanArea')?.value || '';
  const foremanArea = defaultForemanArea;
  const date = form.fields.find(f => f.field === 'ActualStartTime')?.value || form.createdAt;
  const formattedDate = formatDate(date);

  // Get employee name
  const employeeName = getFormEmployeeDisplayName(form);

  // Get SubcontractorNameCom for the title
  const subcontractorNameCom = form.fields.find(f => 
    f.path.includes('SubcontractorNameCom') || f.field === 'SubcontractorNameCom'
  )?.value;

  // Determine if it's a Hired Equipment form or Daily Log
  const isHiredEquipmentForm = form.fields.some(field => 
    field.path.includes('EquipmentTimeWorkedJ') || 
    field.field === 'AmountBilled' ||
    field.path.includes('SubcontractorNameCom')
  );
  const isMechanicForm = !isHiredEquipmentForm && isMechanicDailyLog(form);
  const showAccomplishmentColumn = !isMechanicForm;
  const summaryTableColCount = showAccomplishmentColumn ? 12 : 11;
  const formType = isHiredEquipmentForm
    ? 'Hired Equipment'
    : isMechanicForm
      ? 'Daily Log - Mechanic'
      : 'Daily Log - RB';
  // Create title based on form type
  let headerTitle;
  if (isHiredEquipmentForm && subcontractorNameCom) {
    // For hired equipment: Subcontractor - Employee
    const employeeDisplayName = `${subcontractorNameCom} - ${employeeName}`;
    headerTitle = `${employeeDisplayName} | Hours: ${0} | Cost: $${0}`;  // Will be updated below
  } else if (subcontractorNameCom) {
    // For daily log with subcontractor: Employee - Subcontractor
    const employeeDisplayName = `${employeeName} - ${subcontractorNameCom}`;
    headerTitle = `${employeeDisplayName} | Hours: ${0} | Cost: $${0}`;  // Will be updated below
  } else {
    // No subcontractor: just employee name
    const employeeDisplayName = employeeName;
    headerTitle = `${employeeDisplayName} | Hours: ${0} | Cost: $${0}`;  // Will be updated below
  }

  // Create subtitle: "Form Type | Foreman Area | Date" (removed subcontractor since it's in title)
  let subtitle = formType;
  subtitle += ` | ${foremanArea} | ${formattedDate}`;

  // Get start time for the subtitle
  const startTime = form.fields.find(f => f.field === 'ActualStartTime')?.value;
  if (startTime) {
    try {
      const startTimeDate = new Date(startTime);
      if (!isNaN(startTimeDate.getTime())) {
        const hours = String(startTimeDate.getHours()).padStart(2, '0');
        const minutes = String(startTimeDate.getMinutes()).padStart(2, '0');
        const startTimeFormatted = `${hours}:${minutes}`;
        subtitle += ` | Start Time: ${startTimeFormatted}`;
      }
    } catch (error) {
      console.error('Error formatting start time:', error);
    }
  }

  // Group fields by task
  const taskGroups = form.fields.reduce((groups: { [key: string]: Form['fields'] }, field) => {
    // Look for fields within WorkCompleted section
    if (!field.path.includes('WorkCompleted')) return groups;
    
    // Extract task group from the path (e.g., "Tasks 1.1" from "WorkCompleted.Tasks 1.1.Tasks.Task 3.Task")
    const taskMatch = field.path.match(/WorkCompleted\.Tasks (\d+\.\d+)/);
    if (!taskMatch) return groups;
    
    const taskGroupId = taskMatch[1];
    if (!groups[taskGroupId]) {
      groups[taskGroupId] = [];
    }
    groups[taskGroupId].push(field);
    return groups;
  }, {});

  // For hired equipment forms, the TimeWorkedJobCost fields are already included in the WorkCompleted groups
  // since they have paths like "WorkCompleted.Tasks 1.0.Activity.TimeWorkedJobCost 1.0.EquipmentTimeWorkedJ"
  if (isHiredEquipmentForm) {
    
  }

  // Process each task group (Mechanic uses RB-style labour / vehicle / meal rows from form fields)
  const taskEfforts: TaskEffort[] = isMechanicForm
    ? (() => {
        const efforts = buildMechanicEffortSummaries(form).map((e) => {
          if (e.type !== 'Labour') {
            return e as EffortSummary;
          }
          const lineKey = e.mechanicGroupId
            ? e.mechanicGroupId === 'form'
              ? MECHANIC_SUBMISSION_LABOUR_LINE_KEY_FALLBACK
              : buildMechanicSubmissionLabourLineKey(e.mechanicGroupId)
            : undefined;
          const otBreakdown = lineKey
            ? aggregateFormLabourOtBreakdownDisplay(form.id, 0, availableTasks, {
                labourOvertimeAllocations,
                lineKey
              })
            : undefined;
          const totalCost =
            lineKey && otBreakdown && otBreakdown !== '–'
              ? resolveLabourLineCostForFormTask(form.id, 0, availableTasks, e.totalCost, {
                  labourOvertimeAllocations,
                  lineKey
                })
              : e.totalCost;
          return { ...e, otBreakdown, totalCost } as EffortSummary;
        });
        const totalCost = efforts.reduce((sum, e) => sum + e.totalCost, 0);
        return [{ taskId: 'mechanic', efforts, totalCost }];
      })()
    : Object.entries(taskGroups).map(([taskGroupId, fields]) => {
    
    const efforts: EffortSummary[] = [];
    
    // All groups are now WorkCompleted groups, but some may contain TimeWorkedJobCost fields
    const hasTimeWorkedJobCostFields = fields.some(f => f.path.includes('TimeWorkedJobCost'));
    
    let taskNumber = 0;
    let taskDesc = '';
    let taskName = '';
    let assetInfo = '';
    let assetNumber = '';
    let taskNumberDisplay = '';

    // Extract task information from WorkCompleted fields
    const taskIdField = fields.find(f => f.field === 'TaskId' || f.path.match(/\.TaskId$/));
    const taskField = fields.find(f => f.field === 'Task' && f.path.endsWith('.Task'));
    const workCompletedId = fields.find(f => f.field === 'WorkCompletedID')?.value;
    
    // Determine task ID and description
    const rawTaskId = taskIdField?.value || taskField?.value || workCompletedId || '0';
    taskNumber = parseInt(rawTaskId);
    taskDesc = taskField?.value || `Task ${taskGroupId}`;
    
    // Get task name from available tasks if we have a task ID
    taskName = fields.find(f => f.field === 'TaskName')?.value || '';
    
    // Extract SubcontractorOrHERep field value to use as Type for subcontractor efforts
    // Check task-specific fields first, then form-level fields as fallback
    const subcontractorOrHERep = fields.find(f => 
      f.field === 'SubcontractorOrHERep' || 
      f.path.includes('SubcontractorOrHERep')
    )?.value || form.fields.find(f => 
      f.field === 'SubcontractorOrHERep' || 
      f.path.includes('SubcontractorOrHERep')
    )?.value;
    
    // Use SubcontractorOrHERep value as type, fallback to 'Hired Equipment' if not found
    // Handle array values (the field might have values as an array)
    let subcontractorTypeValue = subcontractorOrHERep;
    if (Array.isArray(subcontractorOrHERep)) {
      subcontractorTypeValue = subcontractorOrHERep[0];
    }
    const subcontractorType = subcontractorTypeValue && String(subcontractorTypeValue).trim() 
      ? String(subcontractorTypeValue).trim() 
      : 'Hired Equipment';
    
    // Debug log for task lookup

    // Look up task name and asset from available tasks if we have a task number
    if (taskNumber) {
      const task = availableTasks.find(t => t.id === taskNumber);
      if (task) {
        // First try to use the task name, then fall back to description
        taskName = task.name || task.description || taskName;
        taskNumberDisplay = task.taskNumber || taskNumber.toString();
        
        // Check if this form has rest area fields - if so, prioritize form field extraction
        // over task database asset to ensure correct rest area is shown
        const hasRestAreaFields = form.fields.some(f => 
          f.field === 'RestArea 1' || f.field === 'RestAreaNumber 1'
        );
        
        if (hasRestAreaFields) {
          
          // Try to extract asset information from form fields first
          
          
          const assetFromFields = getAssetForTaskGroup(taskGroupId, form.fields);
          if (assetFromFields) {
            assetInfo = assetFromFields.name;
            assetNumber = assetFromFields.assetNumber;
          } else {
            
            // Fallback to task asset if form field extraction fails
            if (task.taskLocations && task.taskLocations.length > 0 && task.taskLocations[0].asset) {
              const firstAsset = task.taskLocations[0].asset;
              assetInfo = firstAsset.name;
              assetNumber = firstAsset.assetNumber;
            }
          }
        } else {
          // For non-rest-area forms, use the original logic (task asset first, then form fields)
          if (task.taskLocations && task.taskLocations.length > 0 && task.taskLocations[0].asset) {
            const firstAsset = task.taskLocations[0].asset;
            assetInfo = firstAsset.name;
            assetNumber = firstAsset.assetNumber;
          } else {
            
            // Fallback: Try to extract asset information from form fields
            
            
            const assetFromFields = getAssetForTaskGroup(taskGroupId, form.fields);
            if (assetFromFields) {
              assetInfo = assetFromFields.name;
              assetNumber = assetFromFields.assetNumber;
            } else {
            }
          }
        }
        
      }
    }

    // If still no task name, try to find it in the task fields
    if (!taskName) {
      // Try TaskName field first
      const taskNameField = fields.find(f => f.field === 'TaskName');
      if (taskNameField) {
        taskName = taskNameField.value;
      } else {
        // Then try Task field that's not a TaskId
        const taskField = fields.find(f => 
          f.field === 'Task' && 
          !f.path.includes('TaskId')
        );
        if (taskField) {
          taskName = taskField.value;
        }
      }
    }

    // If we still don't have a name, use the task description
    if (!taskName) {
      taskName = taskDesc;
    }

    // Primary Vehicle Usage
    const primaryVehicleNumber = fields.find(f => f.field === 'OtherVehicleDescri 1')?.value || 
                                  fields.find(f => f.field === 'VehicleUnitNumber 1')?.value;
    const primaryVehicleRate = fields.find(f => f.field === 'VehicleRate')?.value;
    const primaryVehicleHours = fields.find(f => f.field === 'HoursVehicleUsage')?.value;
    
    if (primaryVehicleNumber && primaryVehicleHours) {
        efforts.push({
          type: 'Vehicle',
          description: `Unit #${primaryVehicleNumber}`,
          quantity: safeParseFloat(primaryVehicleHours),
          rate: safeParseFloat(primaryVehicleRate, 1),
          totalCost: safeParseFloat(primaryVehicleHours) * safeParseFloat(primaryVehicleRate, 1),
          unitOfMeasure: 'Hours',
          task: taskNumberDisplay || taskDesc,
          taskName: taskName,
          asset: assetInfo,
          assetNumber: assetNumber,
          homeForemanAreaId: form.homeForemanAreaId,
          taskId: taskNumber
        });
    }

    // Secondary Vehicle Usage
    const secondaryVehicleNumber = fields.find(f => f.field === 'OtherVehicleDescri 2')?.value || 
                                    fields.find(f => f.field === 'SecondaryUnitNumber')?.value;
    const secondaryVehicleRate = fields.find(f => f.field === 'SecondaryVehicleRate')?.value;
    const secondaryVehicleHours = fields.find(f => f.field === 'Hours Secondary Vehi')?.value;
    
    if (secondaryVehicleNumber && secondaryVehicleHours) {
      efforts.push({
        type: 'Vehicle',
        description: `Unit #${secondaryVehicleNumber}`,
        quantity: safeParseFloat(secondaryVehicleHours),
        rate: safeParseFloat(secondaryVehicleRate, 1),
        totalCost: safeParseFloat(secondaryVehicleHours) * safeParseFloat(secondaryVehicleRate, 1),
        unitOfMeasure: 'Hours',
        task: taskNumberDisplay || taskDesc,
        taskName: taskName,
        asset: assetInfo,
        assetNumber: assetNumber,
        homeForemanAreaId: form.homeForemanAreaId,
        taskId: taskNumber
      });
    }

    // Materials - handle multiple
    const materials = fields.filter(f => f.field === 'MaterialUsed 4');
    materials.forEach(material => {
      const materialPath = material.path;
      const basePath = materialPath.substring(0, materialPath.lastIndexOf('.'));
      
      const quantity = fields.find(f => f.path.startsWith(basePath) && f.field === 'QuantityUsed 1')?.value;
      const rate = fields.find(f => f.path.startsWith(basePath) && f.field === 'MaterialRate')?.value || '0';
      const unitOfMeasure = fields.find(f => f.path.startsWith(basePath) && f.field === 'UnitsMeasurement 6')?.value || 'Units';
      const comments = fields.find(f => f.path.startsWith(basePath) && f.field === 'Comments 4')?.value;
      
      if (quantity) {
        efforts.push({
          type: 'Material',
          description: material.value || 'Unknown Material',
          quantity: safeParseFloat(quantity),
          rate: safeParseFloat(rate, 1),
          totalCost: safeParseFloat(quantity) * safeParseFloat(rate, 1),
          unitOfMeasure: unitOfMeasure,
          task: taskNumberDisplay || taskDesc,
          taskName: taskName,
          asset: assetInfo,
          assetNumber: assetNumber,
          homeForemanAreaId: form.homeForemanAreaId,
          taskId: taskNumber,
          comments: comments
        });
      }
    });

    // Labour - search with the correct path structure
    const labourHours = fields.find(f => 
      f.field === 'TaskDurationHours' && 
      f.path.includes('Timecard and Job C')
    )?.value || '0';

    const labourRate = fields.find(f => f.field === 'ClassRate')?.value;
    const employeeClass = fields.find(f => f.field === 'EmployeeClass 2')?.value || 'Labour';
    const labourComments = fields.find(f => f.field === 'Comments 5')?.value;

    const labourQty = safeParseFloat(labourHours);
    const taskAllocRows = labourOvertimeAllocations.filter(
      (a) => a.originalTaskId === taskNumber
    );
    const hasTaskAllocationRows = taskAllocRows.length > 0;
    // Match server `submissionLabourLines.buildOrderedSubmissionLabourLines`: scope OT/cost to this WC line.
    const lineKey =
      taskNumber > 0 ? `WorkCompleted.Tasks ${taskGroupId}|task=${taskNumber}` : undefined;
    const labourMerge = {
      taskName,
      persistedLabourForForm: persistedLabourEfforts,
      labourOvertimeAllocations,
      lineKey,
    };

    // Only add labour effort when TaskDurationHours indicates hours (DRM allocations alone do not add a row)
    if (labourQty > 0) {
      
      const rate = labourRate ? safeParseFloat(labourRate) : 0;
      const fallbackLabourCost = rate * labourQty;
      const otBreakdown = aggregateFormLabourOtBreakdownDisplay(
        form.id,
        taskNumber,
        availableTasks,
        labourMerge
      );
      const labourTotalCost = hasTaskAllocationRows
        ? resolveLabourLineCostForFormTask(
            form.id,
            taskNumber,
            availableTasks,
            fallbackLabourCost,
            labourMerge
          )
        : fallbackLabourCost;
      
      efforts.push({
        type: 'Labour',
        description: employeeClass,
        quantity: labourQty,
        rate: rate,
        totalCost: labourTotalCost,
        unitOfMeasure: 'Hours',
        task: taskNumberDisplay || taskDesc,
        taskName: taskName,
        asset: assetInfo,
        assetNumber: assetNumber,
        homeForemanAreaId: form.homeForemanAreaId,
        taskId: taskNumber,
        comments: labourComments,
        otBreakdown,
      });
    }

    // Subcontractor Labour from TimeWorkedJobCost
    const subcontractorFields = fields.filter(f => 
      f.path.includes('TimeWorkedJobCost') && 
      f.path.includes('EquipmentTimeWorkedJ')
    );

    // === Robust Subcontractor Name Extraction (matching backend logic) ===
    // Try all possible field names and paths for subcontractor name
    // Look in both task-specific fields and main form fields
    const possibleSubNames = [
      // Look in task-specific fields first
      fields.find(f => f.field === 'SubcontractorNameCom')?.value,
      fields.find(f => f.path && f.path.toLowerCase().includes('subcontractornamecom'))?.value,
      fields.find(f => f.path.includes('SubcontractorInforma.SubcontractorNameCom'))?.value,
      fields.find(f => f.path.includes('SubcontractorName'))?.value,
      fields.find(f => f.field === 'SubcontractorName')?.value,
      fields.find(f => f.path && f.path.toLowerCase().includes('subcontractorname'))?.value,
      fields.find(f => f.field && f.field.startsWith('SubcontractorName'))?.value,
      fields.find(f => f.path && f.path.toLowerCase().includes('subcontractorname'))?.value,
      // Then look in main form fields as fallback
      form.fields.find(f => f.field === 'SubcontractorNameCom')?.value,
      form.fields.find(f => f.path && f.path.toLowerCase().includes('subcontractornamecom'))?.value,
      form.fields.find(f => f.path.includes('SubcontractorInforma.SubcontractorNameCom'))?.value,
      form.fields.find(f => f.field === 'SubcontractorName')?.value,
      form.fields.find(f => f.path && f.path.toLowerCase().includes('subcontractorname'))?.value,
      form.fields.find(f => f.field && f.field.startsWith('SubcontractorName'))?.value,
      form.fields.find(f => f.path && f.path.toLowerCase().includes('subcontractorname'))?.value
    ];
    const subcontractorNameFinal = possibleSubNames.find(v => v && v.trim());

    // Process multiple TimeWorkedJobCost entries if they exist
    if (hasTimeWorkedJobCostFields) {
      // Group TimeWorkedJobCost fields by their equipment group (e.g., "0", "1", etc.)
      // NOTE: TimeWorkedJobCost fields are at the FORM level, not within WorkCompleted groups
      const equipmentGroups: { [key: string]: Form['fields'] } = {};
      
      // Search only in the current task's fields to ensure each task gets its own equipment
      // This prevents equipment data from being copied between different tasks
      fields.forEach((field: any) => {
        if (field.path.includes('TimeWorkedJobCost 1.')) {
          // Extract equipment group from path (e.g., "0" from "TimeWorkedJobCost 1.0.EquipmentTimeWorkedJ...")
          // Also match deeper nested paths like "TimeWorkedJobCost 1.0.Costing.Overtime8HourRate"
          const equipmentMatch = field.path.match(/TimeWorkedJobCost 1\.(\d+)(?:\.|$)/);
          if (equipmentMatch) {
            const equipmentGroupId = equipmentMatch[1];
            if (!equipmentGroups[equipmentGroupId]) {
              equipmentGroups[equipmentGroupId] = [];
            }
            equipmentGroups[equipmentGroupId].push(field);
          }
        }
      });


      // Process each equipment group
      Object.entries(equipmentGroups).forEach(([equipmentGroupId, equipmentFields]) => {
        // Check if this equipment group belongs to the current task
        const equipmentTaskId = equipmentFields.find(f => f.field === 'Task')?.value;
        const equipmentTaskIdNum = equipmentTaskId ? parseInt(equipmentTaskId) : 0;
        
        
        // Only process if it belongs to the current task (or if there's no task ID)
        if (equipmentTaskIdNum !== 0 && equipmentTaskIdNum !== taskNumber) {
          return;
        }
        
        // Extract breakdown fields for hired equipment
        const hoursLabour = parseFloat(equipmentFields.find(f => f.field === 'HoursLabour')?.value || '0');
        const rateBlueBook = parseFloat(
          equipmentFields.find(f => f.field === 'RateBlueBook')?.value ||
          equipmentFields.find(f => f.field === 'RateBlueBookPrim')?.value ||
          equipmentFields.find(f => f.field === 'RateBlueBookSec')?.value || '0'
        );
        const fuelPriceAdjustment = parseFloat(
          equipmentFields.find(f => f.field === 'FuelPriceAdjustment')?.value ||
          equipmentFields.find(f => f.field === 'FuelPriceAdjustmentS')?.value ||
          equipmentFields.find(f => f.field === 'PrimaryFuelPriceAdju')?.value || '0'
        );
        // Extract overtime rates - search in equipmentFields first, then task-level fields, then form-level fields
        // Find 8HourRate field (case-insensitive)
        const findOvertime8HourRate = () => {
          const allFields = [...equipmentFields, ...fields, ...form.fields];
          
          for (const field of allFields) {
            const fieldName = (field.field || '').toLowerCase();
            
            // Only check for "8hourrate" (case-insensitive)
            if (fieldName === '8hourrate') {
              const valueStr = String(field.value || '').replace(/[^0-9.]/g, '');
              const value = parseFloat(valueStr);
              if (value > 0) {
                return valueStr;
              }
            }
          }
          
          return '0';
        };
        
        const findOvertime12HourRate = () => {
          // Search in equipmentFields - check both field name and question text
          for (const field of equipmentFields) {
            const fieldName = (field.field || '').toLowerCase();
            const question = (field.question || '').toLowerCase();
            const combined = `${fieldName} ${question}`;
            
            // Check exact matches first
            if (field.field === 'Overtime12HourRate' || 
                field.field === 'OvertimeRateAbove12' || 
                field.field === '12HourAdditionalRate' ||
                field.field === 'Overtime additional rate (over 12 hours)') {
              if (field.value && parseFloat(field.value) > 0) {
                return field.value;
              }
            }
            
            // Check if question or field name contains the pattern
            if (combined.includes('overtime') && combined.includes('12') && (combined.includes('rate') || combined.includes('additional'))) {
              // Extract numeric value from field value (might have currency symbols like "$58.20 USD")
              const valueStr = String(field.value || '').replace(/[^0-9.]/g, '');
              const value = parseFloat(valueStr);
              if (value > 0) {
                return valueStr;
              }
            }
            
            // Also check for "over 12 hours" pattern
            if (combined.includes('over') && combined.includes('12') && combined.includes('hour')) {
              const valueStr = String(field.value || '').replace(/[^0-9.]/g, '');
              const value = parseFloat(valueStr);
              if (value > 0) {
                return valueStr;
              }
            }
          }
          
          // Search in task-level fields
          for (const field of fields) {
            const fieldName = (field.field || '').toLowerCase();
            const question = (field.question || '').toLowerCase();
            const combined = `${fieldName} ${question}`;
            
            if (field.field === 'Overtime12HourRate' || 
                field.field === 'OvertimeRateAbove12' || 
                field.field === '12HourAdditionalRate' ||
                field.field === 'Overtime additional rate (over 12 hours)') {
              if (field.value && parseFloat(field.value) > 0) {
                return field.value;
              }
            }
            
            if (combined.includes('overtime') && combined.includes('12') && (combined.includes('rate') || combined.includes('additional'))) {
              const valueStr = String(field.value || '').replace(/[^0-9.]/g, '');
              const value = parseFloat(valueStr);
              if (value > 0) {
                return valueStr;
              }
            }
            
            if (combined.includes('over') && combined.includes('12') && combined.includes('hour')) {
              const valueStr = String(field.value || '').replace(/[^0-9.]/g, '');
              const value = parseFloat(valueStr);
              if (value > 0) {
                return valueStr;
              }
            }
          }
          
          // Search in form-level fields
          for (const field of form.fields) {
            const fieldName = (field.field || '').toLowerCase();
            const question = (field.question || '').toLowerCase();
            const combined = `${fieldName} ${question}`;
            
            if (field.field === 'Overtime12HourRate' || 
                field.field === 'OvertimeRateAbove12' || 
                field.field === '12HourAdditionalRate' ||
                field.field === 'Overtime additional rate (over 12 hours)') {
              if (field.value && parseFloat(field.value) > 0) {
                return field.value;
              }
            }
            
            if (combined.includes('overtime') && combined.includes('12') && (combined.includes('rate') || combined.includes('additional'))) {
              const valueStr = String(field.value || '').replace(/[^0-9.]/g, '');
              const value = parseFloat(valueStr);
              if (value > 0) {
                return valueStr;
              }
            }
            
            if (combined.includes('over') && combined.includes('12') && combined.includes('hour')) {
              const valueStr = String(field.value || '').replace(/[^0-9.]/g, '');
              const value = parseFloat(valueStr);
              if (value > 0) {
                return valueStr;
              }
            }
          }
          
          return '0';
        };
        
        let overtime8HourRate = parseFloat(findOvertime8HourRate());
        let overtime12HourRate = parseFloat(findOvertime12HourRate());
        
        // Fallback: If hours are between 8-12 and we only have the 12-hour rate, use it for 8-12 overtime
        // This handles cases where the form only has one overtime rate field
        if (hoursLabour > 8 && hoursLabour <= 12 && overtime8HourRate === 0 && overtime12HourRate > 0) {
          overtime8HourRate = overtime12HourRate;
        }
        
        // Use AttachmentTotal - should now be captured in equipmentFields
        let attachmentTotal = parseFloat(equipmentFields.find(f => f.field === 'AttachmentTotal')?.value || '0');
        
        // Get CalculatedCost for fallback calculation
        const calculatedCostField = equipmentFields.find(f => f.field === 'CalculatedCost' || f.path.includes('CalculatedCost.CalculatedCost'));
        const calculatedCost = calculatedCostField ? parseFloat(calculatedCostField.value || '0') : 0;
        
        // Get equipment name - prioritize equipment-specific fields over form-level fields
        // Look for SubcontractorName specifically within THIS equipment group first
        const equipmentSpecificName = equipmentFields.find(f => f.field === 'SubcontractorName')?.value ||
                                     equipmentFields.find(f => f.field === 'VehicleUnitNumber')?.value ||
                                     equipmentFields.find(f => f.field === 'PrimaryVehicleType')?.value;
        
        const equipmentName = equipmentSpecificName || subcontractorNameFinal || 'Equipment';
        
        // Get comments from equipment fields or task fields
        const comments = equipmentFields.find(f => f.field === 'Comments 5')?.value ||
                        equipmentFields.find(f => f.field === 'Comments 3')?.value ||
                        equipmentFields.find(f => f.field === 'Comments')?.value ||
                        fields.find(f => f.field === 'Comments 5')?.value ||
                        fields.find(f => f.field === 'Comments 3')?.value ||
                        fields.find(f => f.field === 'Comments')?.value ||
                        '';

        // Log all fields that might contain overtime rates for debugging
        const allOvertimeRelatedFields = [
          ...equipmentFields.filter(f => {
            const fieldName = (f.field || '').toLowerCase();
            return fieldName.includes('overtime') || fieldName.includes('hour') || fieldName.includes('rate') || fieldName.includes('8') || fieldName.includes('12');
          }),
          ...fields.filter(f => {
            const fieldName = (f.field || '').toLowerCase();
            return fieldName.includes('overtime') || fieldName.includes('hour') || fieldName.includes('rate') || fieldName.includes('8') || fieldName.includes('12');
          }),
          ...form.fields.filter(f => {
            const fieldName = (f.field || '').toLowerCase();
            return fieldName.includes('overtime') || fieldName.includes('hour') || fieldName.includes('rate') || fieldName.includes('8') || fieldName.includes('12');
          })
        ].map(f => ({ field: f.field, value: f.value, path: f.path }));


        // Calculate dollar amounts for breakdown components
        const fuelTotal = hoursLabour * fuelPriceAdjustment;
        // Overtime 8-12: only if hoursLabour > 8 and <= 12
        const overtime812Total = (hoursLabour > 8 && hoursLabour <= 12) ? (hoursLabour - 8) * overtime8HourRate : 0;
        // Overtime over 12: only if hoursLabour > 12
        const overtimeAbove12Total = hoursLabour > 12 ? (hoursLabour - 12) * overtime12HourRate : 0;
        
        // If AttachmentTotal is not found but we have CalculatedCost, derive it
        // IMPORTANT: Only derive AttachmentTotal if we don't have overtime rates, otherwise overtime should be shown separately
        if (attachmentTotal === 0 && calculatedCost > 0 && hoursLabour > 0) {
          // CalculatedCost = (HoursLabour × (RateBlueBook + FuelPriceAdjustmentS)) + OT8-12 + OT12+ + AttachmentTotal
          // So: AttachmentTotal = CalculatedCost - (HoursLabour × (RateBlueBook + FuelPriceAdjustmentS)) - OT8-12 - OT12+
          const hoursCost = hoursLabour * (rateBlueBook + fuelPriceAdjustment);
          
          // Only subtract overtime if we actually found overtime rates (otherwise it might be included in AttachmentTotal incorrectly)
          let overtimeToSubtract = 0;
          if (hoursLabour > 8 && hoursLabour <= 12 && overtime8HourRate > 0) {
            overtimeToSubtract = overtime812Total;
          } else if (hoursLabour > 12 && overtime12HourRate > 0) {
            overtimeToSubtract = overtimeAbove12Total;
          }
          
          attachmentTotal = calculatedCost - hoursCost - overtimeToSubtract;
          
          // Ensure it's not negative (rounding errors or different calculation method)
          if (attachmentTotal < 0) {
            attachmentTotal = 0;
          }
          
        }
        
        // Calculate expected total cost
        const calculatedTotal = (hoursLabour * (rateBlueBook + fuelPriceAdjustment)) + overtime812Total + overtimeAbove12Total + attachmentTotal;
        
        // Check if we have any data to process
        const hasHours = hoursLabour > 0 && rateBlueBook > 0;
        const hasAttachments = attachmentTotal > 0;


        // Resolve UOM from this equipment group only — prefer UnitsMeasurement
        // (Subcontractor Selection). Do not use form-wide UnitsMeasurement 4 (activity UOM).
        const equipmentUom =
          equipmentFields.find(f => f.field === 'UnitsMeasurement')?.value ||
          equipmentFields.find(f => f.field === 'UnitsMeasurementPrim')?.value ||
          equipmentFields.find(f => f.field === 'UnitsMeasurement 4')?.value ||
          'Hours';

        // Process hours-based entries (Regular Hours, Overtime, Fuel)
        if (hasHours) {
          // Create Regular Hours effort (without breakdown in description)
          const regularHoursDesc = `${equipmentName} - Regular Hours`;
          
          efforts.push({
            type: subcontractorType,
            description: regularHoursDesc,
            quantity: hoursLabour,
            rate: rateBlueBook,
            totalCost: hoursLabour * rateBlueBook,
            unitOfMeasure: equipmentUom,
            task: taskNumberDisplay || taskDesc,
            taskName: taskName,
            asset: assetInfo,
            assetNumber: assetNumber,
            homeForemanAreaId: form.homeForemanAreaId,
            taskId: taskNumber,
            comments: comments
          });

          // Create Overtime Hours (8-12) effort if applicable
          // Only show if hoursLabour > 8 and <= 12
          if (hoursLabour > 8 && hoursLabour <= 12 && overtime8HourRate > 0) {
            const hoursBetween812 = hoursLabour - 8;
            const overtimeDesc = `${equipmentName} - Overtime 8-12`;
            efforts.push({
              type: subcontractorType,
              description: overtimeDesc,
              quantity: hoursBetween812,
              rate: overtime8HourRate,
              totalCost: hoursBetween812 * overtime8HourRate,
              unitOfMeasure: equipmentUom,
              task: taskNumberDisplay || taskDesc,
              taskName: taskName,
              asset: assetInfo,
              assetNumber: assetNumber,
              homeForemanAreaId: form.homeForemanAreaId,
              taskId: taskNumber,
              comments: comments || '',
              excludeFromQuantityTotal: true
            });
          }

          // Create Overtime (over 12 hours) effort if applicable
          if (hoursLabour > 12 && overtime12HourRate > 0) {
            const overtimeHoursOver12 = hoursLabour - 12;
            const overtimeDesc = `${equipmentName} - Overtime 12`;
            efforts.push({
              type: subcontractorType,
              description: overtimeDesc,
              quantity: overtimeHoursOver12,
              rate: overtime12HourRate,
              totalCost: overtimeHoursOver12 * overtime12HourRate,
              unitOfMeasure: equipmentUom,
              task: taskNumberDisplay || taskDesc,
              taskName: taskName,
              asset: assetInfo,
              assetNumber: assetNumber,
              homeForemanAreaId: form.homeForemanAreaId,
              taskId: taskNumber,
              comments: comments || '',
              excludeFromQuantityTotal: true
            });
          }

          // Create Fuel Surcharge effort if applicable
          if (fuelPriceAdjustment > 0) {
            const fuelDesc = `${equipmentName} - Fuel Surcharge ($${fuelTotal.toFixed(2)})`;
            efforts.push({
              type: subcontractorType,
              description: fuelDesc,
              quantity: hoursLabour,
              rate: fuelPriceAdjustment,
              totalCost: hoursLabour * fuelPriceAdjustment,
              unitOfMeasure: equipmentUom,
              task: taskNumberDisplay || taskDesc,
              taskName: taskName,
              asset: assetInfo,
              assetNumber: assetNumber,
              homeForemanAreaId: form.homeForemanAreaId,
              taskId: taskNumber,
              comments: comments ? `${comments} (Fuel)` : 'Fuel price adjustment',
              excludeFromQuantityTotal: true
            });
          }
        }

        // Create Attachments effort if applicable (INDEPENDENT of hours)
        // BUT: Don't create attachments if we should have overtime but the rate wasn't found
        // This prevents showing "Attachments" when it should be "Overtime"
        const shouldHaveOvertimeButRateNotFound = 
          (hoursLabour > 8 && hoursLabour <= 12 && overtime8HourRate === 0) ||
          (hoursLabour > 12 && overtime12HourRate === 0);
        
        if (hasAttachments && !shouldHaveOvertimeButRateNotFound) {
          const attachmentDesc = `${equipmentName} - Attachments ($${attachmentTotal.toFixed(2)})`;
          efforts.push({
            type: subcontractorType,
            description: attachmentDesc,
            quantity: 1,
            rate: attachmentTotal,
            totalCost: attachmentTotal,
            unitOfMeasure: 'Lump Sum',
            task: taskNumberDisplay || taskDesc,
            taskName: taskName,
            asset: assetInfo,
            assetNumber: assetNumber,
            homeForemanAreaId: form.homeForemanAreaId,
            taskId: taskNumber,
            comments: comments ? `${comments} (Attachments)` : 'Multiple equipment attachments',
            excludeFromQuantityTotal: true
          });
        } else if (hasAttachments && shouldHaveOvertimeButRateNotFound) {
          console.warn('⚠️ Should show Overtime but rate not found! AttachmentTotal might include overtime cost:', {
            hoursLabour,
            attachmentTotal,
            overtime8HourRate,
            overtime12HourRate,
            shouldShowOvertime812: hoursLabour > 8 && hoursLabour <= 12,
            shouldShowOvertime12: hoursLabour > 12
          });
        }

        // If no breakdown data at all, use fallback
        if (!hasHours && !hasAttachments) {
          // Fallback to old logic if no breakdown data
          const calculatedCost = equipmentFields.find(f => 
            f.path.includes('CalculatedCost.CalculatedCost')
          )?.value;
          const amountBilled = form.fields.find(f => 
            f.path.includes('WorkCompletedCosting.AmountBilled') &&
            f.field === 'AmountBilled'
          )?.value;

          let subcontractorCost = 0;
          if (calculatedCost && parseFloat(calculatedCost) > 0) {
            subcontractorCost = parseFloat(calculatedCost);
          } else if (amountBilled && parseFloat(amountBilled) > 0) {
            subcontractorCost = parseFloat(amountBilled);
          }

          // Resolve UOM from this equipment group only — prefer UnitsMeasurement
          // (Subcontractor Selection). Do not use form-wide UnitsMeasurement 4 (activity UOM).
          const fallbackUom =
            equipmentFields.find(f => f.field === 'UnitsMeasurement')?.value ||
            equipmentFields.find(f => f.field === 'UnitsMeasurementPrim')?.value ||
            equipmentFields.find(f => f.field === 'UnitsMeasurement 4')?.value ||
            'Hours';

          if (subcontractorCost > 0) {
            efforts.push({
              type: subcontractorType,
              description: subcontractorNameFinal || 'Unknown Subcontractor',
              quantity: 1,
              rate: subcontractorCost,
              totalCost: subcontractorCost,
              unitOfMeasure: fallbackUom,
              task: taskNumberDisplay || taskDesc,
              taskName: taskName,
              asset: assetInfo,
              assetNumber: assetNumber,
              homeForemanAreaId: form.homeForemanAreaId,
              taskId: taskNumber,
              comments: comments || 'Total cost'
            });
          }
        }
      });
    } else {
      // Original subcontractor logic for non-equipment forms
      // Find calculated cost and subcontractor name using the specific paths
      const calculatedCost = fields.find(f => 
        f.path.includes('CalculatedCost.CalculatedCost')
      )?.value;

      // Debug log for cost calculation

      // Also look for AmountBilled in WorkCompletedCosting section
      // AmountBilled is a form-level field, so look in main form fields, not task-specific fields
      const amountBilled = form.fields.find(f => 
        f.path.includes('WorkCompletedCosting.AmountBilled') &&
        f.field === 'AmountBilled'
      )?.value;

      // Determine which cost to use - prefer CalculatedCost, fallback to AmountBilled
      let subcontractorCost = 0;
      let costSource = '';
      if (calculatedCost && parseFloat(calculatedCost) > 0) {
        subcontractorCost = parseFloat(calculatedCost);
        costSource = 'CalculatedCost';
      } else if (amountBilled && parseFloat(amountBilled) > 0) {
        subcontractorCost = parseFloat(amountBilled);
        costSource = 'AmountBilled';
      }


      // Extract real values from form (same logic as FormCard)
      let subcontractorQuantity = 1;
      let subcontractorRate = 0;
      
      // Look for quantity in any field that might contain hours/time/quantity
      for (const field of fields) {
        const value = parseFloat(field.value);
        if (!isNaN(value) && value > 0) {
          if (!field.field.toLowerCase().includes('calculated') && 
              !field.field.toLowerCase().includes('total') &&
              (field.field.toLowerCase().includes('hour') || 
               field.field.toLowerCase().includes('time') ||
               field.field.toLowerCase().includes('duration') ||
               field.field.toLowerCase().includes('quantity'))) {
            subcontractorQuantity = value;
            break;
          }
        }
      }
      
      // Look for rate in any field that might contain rate/price per unit
      for (const field of fields) {
        const value = parseFloat(field.value);
        if (!isNaN(value) && value > 0) {
          if (!field.field.toLowerCase().includes('calculated') && 
              !field.field.toLowerCase().includes('total') &&
              (field.field.toLowerCase().includes('rate') || 
               field.field.toLowerCase().includes('price') ||
               field.field.toLowerCase().includes('cost'))) {
            subcontractorRate = value;
            break;
          }
        }
      }
      
      const subcontractorUnitOfMeasure =
        fields.find(f => f.field === 'UnitsMeasurement 4')?.value ||
        form.fields.find(f => f.field === 'UnitsMeasurement 4')?.value ||
        fields.find(f => f.field === 'UnitsMeasurementPrim')?.value ||
        form.fields.find(f => f.field === 'UnitsMeasurementPrim')?.value ||
        fields.find(f => f.field === 'UnitsMeasurement')?.value ||
        form.fields.find(f => f.field === 'UnitsMeasurement')?.value ||
        'Hours';

      if (subcontractorCost > 0) {
        // Use proper quantity, rate, and unit instead of hardcoded values
        const finalQuantity = subcontractorQuantity > 0 ? subcontractorQuantity : 1;
        const finalRate = subcontractorRate > 0 ? subcontractorRate : subcontractorCost;
        const calculatedTotal = finalQuantity * finalRate;
        const finalCost = calculatedTotal > 0 ? calculatedTotal : subcontractorCost;
        
        
        efforts.push({
          type: subcontractorType,
          description: subcontractorNameFinal || 'Unknown Subcontractor',
          quantity: finalQuantity,
          rate: finalRate,
          totalCost: finalCost,
          unitOfMeasure: subcontractorUnitOfMeasure,
          task: taskNumberDisplay || taskDesc,
          taskName: taskName,
          asset: assetInfo,
          assetNumber: assetNumber,
          homeForemanAreaId: form.homeForemanAreaId,
          taskId: taskNumber,
          comments: costSource === 'AmountBilled' ? 'Fixed Cost' : `Cost source: ${costSource}`
        });
      } else {
      }
    }

    // Add Meal effort if MealDurationMinutes exists and is not zero
    const mealDurationMinutes = fields.find(f => f.field === 'MealDurationMinutes')?.value;
    if (mealDurationMinutes && parseFloat(mealDurationMinutes) > 0) {
      const mealDurationHours = parseFloat(mealDurationMinutes) / 60; // Convert minutes to hours
      efforts.push({
        type: 'Meal',
        description: 'Meal Break',
        quantity: mealDurationHours,
        rate: 0, // Meals typically don't have a rate
        totalCost: 0, // Meals typically don't have a cost
        unitOfMeasure: 'Hours',
        task: taskNumberDisplay || taskDesc,
        taskName: taskName,
        asset: assetInfo,
        assetNumber: assetNumber,
        homeForemanAreaId: form.homeForemanAreaId,
        taskId: taskNumber
      });
    }

    // Debug log final task info

    // One quantity per work-completed line. Several lines can share a standing task id;
    // looking accomplishment up by that id would stamp the last line onto every row.
    const groupAccomplishment = fields
      .filter((f) => f.field === 'QuantityPrimaryActiv')
      .reduce((acc, f) => acc + safeParseFloat(f.value), 0);
    const effortsForGroup =
      groupAccomplishment > 0
        ? efforts.map((effort) => ({ ...effort, accomplishment: groupAccomplishment }))
        : efforts;

    const totalCost = effortsForGroup.reduce((sum, effort) => sum + effort.totalCost, 0);


    return { taskId: taskGroupId, efforts: effortsForGroup, totalCost };
  });

  // Modify the taskEfforts processing to group by type
  const groupedEfforts = taskEfforts.flatMap(task => task.efforts).reduce((groups, effort) => {
    if (!groups[effort.type]) {
      groups[effort.type] = [];
    }
    groups[effort.type].push(effort);
    return groups;
  }, {} as { [key: string]: EffortSummary[] });


  // Calculate totals
  const totalsByType = Object.entries(groupedEfforts).reduce((totals, [type, efforts]) => {
    totals[type] = {
      quantity: efforts.reduce((sum, e) => sum + (e.excludeFromQuantityTotal ? 0 : e.quantity), 0),
      cost: efforts.reduce((sum, e) => sum + e.totalCost, 0),
      unitOfMeasure: efforts[0].unitOfMeasure
    };
    return totals;
  }, {} as { [key: string]: { quantity: number, cost: number, unitOfMeasure: string } });

  const grandTotal = Object.values(totalsByType).reduce((sum, type) => sum + type.cost, 0);
  const totalLabourHours = totalsByType['Labour']?.quantity || 0;



  // Debug: Log the cost calculation to ensure it matches the Grand Total

  // Update headerTitle with actual calculated values
  if (isHiredEquipmentForm && subcontractorNameCom) {
    const employeeDisplayName = `${subcontractorNameCom} - ${employeeName}`;
    headerTitle = `${employeeDisplayName} | Hours: ${totalLabourHours.toFixed(2)} | Cost: $${grandTotal.toFixed(2)}`;
  } else if (subcontractorNameCom) {
    const employeeDisplayName = `${employeeName} - ${subcontractorNameCom}`;
    headerTitle = `${employeeDisplayName} | Hours: ${totalLabourHours.toFixed(2)} | Cost: $${grandTotal.toFixed(2)}`;
  } else {
    const employeeDisplayName = employeeName;
    headerTitle = `${employeeDisplayName} | Hours: ${totalLabourHours.toFixed(2)} | Cost: $${grandTotal.toFixed(2)}`;
  }

  // Build accomplishment rows from task groups (only include rows with at least one accomplishment value)
  const accomplishmentRows: AccomplishmentRow[] = [];
  Object.entries(taskGroups).forEach(([taskGroupId, fields]) => {
    const { taskId, taskName, taskDisplay, assetDisplay } = getTaskAndAssetForGroup(taskGroupId, fields, form, availableTasks);
    const row: AccomplishmentRow = { taskGroupId, taskId, taskDisplay, taskName, assetDisplay };

    let hasAnyValue = false;
    ACCOMPLISHMENT_COLUMNS.forEach(col => {
      if (col.aggregate === 'sum') {
        const sum = fields
          .filter(f => col.fieldNames.includes(f.field))
          .reduce((acc, f) => acc + safeParseFloat(f.value), 0);
        if (sum > 0) hasAnyValue = true;
        (row as unknown as Record<string, unknown>)[col.rowKey] = sum;
      } else {
        const first = col.fieldNames
          .map(name => fields.find(f => f.field === name)?.value)
          .find(v => v != null && String(v).trim() !== '');
        if (first != null && String(first).trim() !== '') {
          hasAnyValue = true;
          (row as unknown as Record<string, unknown>)[col.rowKey] = String(first).trim();
        }
      }
    });
    if (hasAnyValue) {
      accomplishmentRows.push(row);
    }
  });

  const paginateEfforts = (efforts: EffortSummary[]) => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return efforts.slice(startIndex, endIndex);
  };

  return (
    // Desktop keeps its bounded scroller. In the card layout (tablet / phone) the page is the only
    // scroll - the inner 70vh box plus the table's sideways scroll made three nested scroll areas
    // on an iPad (W-iP5).
    <div
      style={cardLayout ? undefined : { maxHeight: '70vh', overflowY: 'auto' }}
      data-testid="daily-log-work-completed"
      data-layout={cardLayout ? 'cards' : 'table'}
    >
      <SectionCard title="Work Completed">
        {!isMechanicForm &&
          validationResult?.warnings?.some((w) => w.code === TASK_ZERO_LABOUR_HOURS_CODE) && (
            <Alert variant="warning" className="tw-mb-3 tw-flex tw-items-start tw-gap-2">
              <i className="bi bi-clock-history tw-shrink-0 tw-mt-1" aria-hidden />
              <div>
                <strong>
                  {validationResult.warnings.filter((w) => w.code === TASK_ZERO_LABOUR_HOURS_CODE)
                    .length === 1
                    ? 'One task has 0 labour hours'
                    : 'Some tasks have 0 labour hours'}
                </strong>
                <ul className="tw-mb-0 tw-mt-2 tw-text-sm tw-pl-3">
                  {validationResult.warnings
                    .filter((w) => w.code === TASK_ZERO_LABOUR_HOURS_CODE)
                    .map((w, idx) => (
                      <li key={`zero-hrs-${w.taskGroupId}-${idx}`}>{w.message}</li>
                    ))}
                </ul>
              </div>
            </Alert>
          )}
        {isMechanicForm && mechanicDefectsFixedByUnit.length > 0 && (
          <div className="tw-mb-4">
            <h6 className="tw-mb-3">
              <i className="bi bi-tools tw-mr-2"></i>
              Defects fixed
            </h6>

            <div className="tw-mb-3">
              <div className="tw-flex tw-flex-col tw-gap-2">
                {mechanicDefectsFixedByUnit.map((unit) => {
                  const expanded = mechanicDefectUnitExpanded[unit.groupId] === true;
                  const toggle = () =>
                    setMechanicDefectUnitExpanded((prev) => ({
                      ...prev,
                      [unit.groupId]: !prev[unit.groupId]
                    }));
                  return (
                    <div key={`mech-defect-unit-${unit.groupId}`} className="tw-border tw-border-line tw-rounded tw-overflow-hidden">
                      <button
                        type="button"
                        className="tw-w-full tw-flex tw-items-center tw-justify-between tw-text-left tw-bg-surface-alt tw-py-2 tw-px-3 tw-border-0 tw-rounded-none hover:tw-bg-surface-hover"
                        onClick={toggle}
                        aria-expanded={expanded}
                      >
                        <span className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                          <i
                            className={`bi ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'} tw-text-content-muted`}
                            aria-hidden
                          />
                          <strong>{unit.unitTitle}</strong>
                          <span className="tw-text-content-muted tw-text-sm">({unit.defects.length} fixed)</span>
                        </span>
                      </button>
                      {expanded && (
                        <div className="tw-px-3 tw-pb-3 tw-pt-0 tw-border-t tw-border-line">
                          <ul className="tw-list-none tw-pl-0 tw-ml-1 tw-mb-0">
                            {unit.defects.map((defect, index) => (
                              <li key={`mech-defect-expanded-${unit.groupId}-${index}`} className="tw-mb-2">
                                <div className="tw-flex tw-items-start">
                                  <i className="bi bi-check-circle-fill tw-text-success tw-mr-2 tw-mt-1"></i>
                                  <div>
                                    <strong>
                                      Defect
                                      <span className="tw-text-content-muted tw-font-normal"> – {unit.workContext}</span>
                                    </strong>
                                    {defect.category && (
                                      <div className="tw-text-content-muted tw-text-sm">Category: {defect.category}</div>
                                    )}
                                    {defect.comment && (
                                      <div className="tw-text-content-muted tw-text-sm">{defect.comment}</div>
                                    )}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Errors and Warnings Section */}
        {validationResult && (validationResult.hasErrors || validationResult.hasWarnings) && (
          <div className="tw-mb-4">
            <h6 className="tw-mb-3">
              <i className="bi bi-exclamation-triangle tw-mr-2"></i>
              Errors and Warnings
            </h6>

            {/* Errors */}
            {validationResult.hasErrors && (
              <div className="tw-mb-3">
                <Alert variant="danger" className="tw-mb-2">
                  <strong>
                    <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
                    Errors ({validationResult.errors.length}):
                  </strong>
                </Alert>
                <ul className="tw-list-none tw-pl-0 tw-ml-4">
                  {validationResult.errors.map((error, index) => (
                    <li key={index} className="tw-mb-2">
                      <div className="tw-flex tw-items-start">
                        <i className="bi bi-x-circle-fill tw-text-danger tw-mr-2 tw-mt-1"></i>
                        <div>
                          <strong>Task {error.taskNumber ? `#${error.taskNumber}` : error.taskId || error.taskGroupId}</strong>
                          {error.taskName && ` - ${error.taskName}`}
                          <div className="tw-text-content-muted tw-text-sm">{error.message}</div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {validationResult.hasWarnings && (
              <div>
                <Alert variant="warning" className="tw-mb-2">
                  <strong>
                    <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
                    Warnings ({validationResult.warnings.length}):
                  </strong>
                </Alert>
                <ul className="tw-list-none tw-pl-0 tw-ml-4">
                  {validationResult.warnings.map((warning, index) => (
                    <li key={index} className="tw-mb-2">
                      <div className="tw-flex tw-items-start">
                        <i className="bi bi-exclamation-triangle-fill tw-text-warning-text tw-mr-2 tw-mt-1"></i>
                        <div>
                          <strong>{warning.taskGroupId === 'form' ? 'Submission' : `Task ${warning.taskNumber ? `#${warning.taskNumber}` : warning.taskId || warning.taskGroupId}`}</strong>
                          {warning.taskName && ` - ${warning.taskName}`}
                          <div className="tw-text-content-muted tw-text-sm">{warning.message}</div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        
        {/* Tablet and phone (W-iP5): one card per entry. The 12-column table clipped Rate and
            hid Total on an iPad in landscape (and Quantity, Rate, Total in portrait) and squeezed
            Comments to a few words a line; a card shows every field at a readable width with the
            total pinned top-right. Desktop keeps the table below. */}
        {cardLayout && (
        <div className="tw-flex tw-flex-col tw-gap-2" data-testid="daily-log-effort-cards">
          {Object.keys(groupedEfforts).length === 0 ? (
            <div className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-4 tw-text-center tw-text-content-muted">
              No efforts recorded for this form
            </div>
          ) : (
            (() => {
              const standardTypes = ['Labour', 'Vehicle', 'Material', 'Subcontractor', 'Meal'];
              const allTypes = new Set(standardTypes);
              Object.keys(groupedEfforts).forEach((t) => allTypes.add(t));
              return Array.from(allTypes).sort((a, b) => {
                const ai = standardTypes.indexOf(a);
                const bi = standardTypes.indexOf(b);
                if (ai !== -1 && bi !== -1) return ai - bi;
                if (ai !== -1) return -1;
                if (bi !== -1) return 1;
                return a.localeCompare(b);
              });
            })()
              .filter((type) => (groupedEfforts[type] || []).length > 0)
              .map((type) => {
                const efforts = groupedEfforts[type] || [];
                return (
                  <React.Fragment key={`m-${type}`}>
                    {efforts.map((effort, index) => {
                      const accomplishment = effort.accomplishment != null && effort.accomplishment !== 0
                        ? Number(effort.accomplishment).toFixed(2)
                        : null;
                      const assetText = effort.asset || effort.assetNumber || null;
                      return (
                        <div
                          key={`m-${type}-${index}`}
                          className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3"
                          data-testid="daily-log-effort-card"
                        >
                          <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                            <div className="tw-min-w-0">
                              <div className="tw-font-semibold tw-text-content">{effort.type}</div>
                              {effort.description && (
                                <div className="tw-mt-0.5 tw-break-words tw-text-sm tw-text-content-secondary">
                                  {effort.description}
                                </div>
                              )}
                            </div>
                            <div className="tw-shrink-0 tw-text-right">
                              <div className="tw-text-xs tw-text-content-muted">Total</div>
                              <div className="tw-font-semibold tw-tabular-nums tw-text-ink">${effort.totalCost.toFixed(2)}</div>
                            </div>
                          </div>
                          <dl className="tw-mb-0 tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1.5 tw-text-sm sm:tw-grid-cols-4">
                            <div className="tw-min-w-0">
                              <dt className="tw-text-xs tw-text-content-muted">{isMechanicForm ? 'Activity Number' : 'Task ID'}</dt>
                              <dd className="tw-mb-0 tw-break-words tw-text-content">{effort.task || '–'}</dd>
                            </div>
                            <div className="tw-min-w-0">
                              <dt className="tw-text-xs tw-text-content-muted">{isMechanicForm ? 'Activity Name' : 'Task Name'}</dt>
                              <dd className="tw-mb-0 tw-break-words tw-text-content">{effort.taskName || '–'}</dd>
                            </div>
                            <div className="tw-min-w-0">
                              <dt className="tw-text-xs tw-text-content-muted">{isMechanicForm ? 'Activity Type' : 'Asset'}</dt>
                              <dd className="tw-mb-0 tw-break-words tw-text-content">{assetText || '–'}</dd>
                            </div>
                            {showAccomplishmentColumn && (
                              <div className="tw-min-w-0">
                                <dt className="tw-text-xs tw-text-content-muted">Accomplishment</dt>
                                <dd className="tw-mb-0 tw-tabular-nums tw-text-content">{accomplishment ?? '–'}</dd>
                              </div>
                            )}
                            <div className="tw-min-w-0">
                              <dt className="tw-text-xs tw-text-content-muted">Quantity</dt>
                              <dd className="tw-mb-0 tw-tabular-nums tw-text-content">
                                {effort.quantity.toFixed(2)} {effort.unitOfMeasure}
                              </dd>
                            </div>
                            <div className="tw-min-w-0">
                              <dt className="tw-text-xs tw-text-content-muted">Rate ($)</dt>
                              <dd className="tw-mb-0 tw-tabular-nums tw-text-content">{effort.rate.toFixed(2)}</dd>
                            </div>
                            {effort.type === 'Labour' && effort.otBreakdown && (
                              <div className="tw-min-w-0 tw-col-span-2">
                                <dt className="tw-text-xs tw-text-content-muted">OT Breakdown</dt>
                                <dd className="tw-mb-0 tw-text-content" style={{ whiteSpace: 'pre-line' }}>{effort.otBreakdown}</dd>
                              </div>
                            )}
                          </dl>
                          {effort.comments && (
                            <div className="tw-mt-2 tw-border-t tw-border-line tw-pt-2 tw-text-sm">
                              <div className="tw-text-xs tw-text-content-muted">Comments</div>
                              <p className="tw-mb-0 tw-break-words tw-text-content">{effort.comments}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-bg-surface-alt tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-content">
                      <span>{type} Total</span>
                      <span className="tw-tabular-nums">
                        {totalsByType[type].quantity.toFixed(2)} {totalsByType[type].unitOfMeasure} · $
                        {totalsByType[type].cost.toFixed(2)}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })
          )}
          {Object.keys(groupedEfforts).length > 0 && (
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-bg-surface-hover tw-px-3 tw-py-2.5 tw-font-bold tw-text-content">
              <span>Grand Total</span>
              <span className="tw-tabular-nums">${grandTotal.toFixed(2)}</span>
            </div>
          )}
        </div>
        )}

        {!cardLayout && (
        <div className="tw-w-full tw-overflow-x-auto">
          <Table size="sm" hover>
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
                <th>{isMechanicForm ? 'Activity number' : 'Task ID'}</th>
                <th>{isMechanicForm ? 'Activity name' : 'Task Name'}</th>
                <th>{isMechanicForm ? 'Activity Type' : 'Asset'}</th>
                {showAccomplishmentColumn && <th>Accomplishment</th>}
                <th>Comments</th>
                <th>OT Breakdown</th>
                <th>Quantity</th>
                <th>Unit</th>
                <th>Rate ($)</th>
                <th className="tw-text-right">Total ($)</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(groupedEfforts).length === 0 ? (
                <tr>
                  <td colSpan={summaryTableColCount} className="tw-text-center tw-text-content-muted tw-py-4">
                    No efforts recorded for this form
                  </td>
                </tr>
              ) : (
                (() => {
                  // Get standard types in order, then add any custom types (like from SubcontractorOrHERep)
                  const standardTypes = ['Labour', 'Vehicle', 'Material', 'Subcontractor', 'Meal'];
                  const allTypes = new Set(standardTypes);
                  // Add any custom types from groupedEfforts that aren't in the standard list
                  Object.keys(groupedEfforts).forEach(type => allTypes.add(type));
                  // Return in order: standard types first, then custom types
                  return Array.from(allTypes).sort((a, b) => {
                    const aIndex = standardTypes.indexOf(a);
                    const bIndex = standardTypes.indexOf(b);
                    // If both are in standard types, maintain their order
                    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                    // Standard types come first
                    if (aIndex !== -1) return -1;
                    if (bIndex !== -1) return 1;
                    // Custom types are sorted alphabetically
                    return a.localeCompare(b);
                  });
                })()
                  .filter(type => {
                    const efforts = groupedEfforts[type] || [];
                    return efforts.length > 0;
                  })
                  .map(type => {
                const efforts = groupedEfforts[type] || [];
                const paginatedEfforts = paginateEfforts(efforts);
                const totalPages = Math.ceil(efforts.length / pageSize);

                return (
                  <React.Fragment key={type}>
                    {paginatedEfforts.map((effort, index) => (
                      <tr key={`${type}-${index}`}>
                        <td>{effort.type}</td>
                        <td>{effort.description}</td>
                        <td>{effort.task}</td>
                        <td>{effort.taskName || '-'}</td>
                         <td>{isMechanicForm ? effort.asset || effort.assetNumber || '-' : (() => {
                           if (!effort.asset && !effort.assetNumber) return '-';
                           
                           // Clean up the asset name to remove duplicate numbers in brackets
                           let cleanAssetName = effort.asset || '';
                           
                           if (cleanAssetName) {
                             // Remove duplicate numbers in brackets at the end
                             // Pattern: "Name (#123) (123)" -> "Name (#123)"
                             cleanAssetName = cleanAssetName.replace(/\s*\([^)]*\)\s*\([^)]*\)$/, '');
                           }
                           
                           // If we have both asset and assetNumber, and they're different
                           if (cleanAssetName && effort.assetNumber && cleanAssetName !== effort.assetNumber) {
                             // Check if the asset name already contains the asset number in brackets
                             const hasNumberInBrackets = cleanAssetName.includes(`(${effort.assetNumber})`);
                             if (hasNumberInBrackets) {
                               return cleanAssetName; // Don't add duplicate brackets
                             } else {
                               // Only add the asset number if it's not already in the name
                               const hasNumberInName = cleanAssetName.includes(effort.assetNumber);
                               if (hasNumberInName) {
                                 return cleanAssetName; // Don't add duplicate
                               } else {
                                 return `${cleanAssetName} (${effort.assetNumber})`;
                               }
                             }
                           }
                           
                           // If we only have asset name, return it
                           if (cleanAssetName) return cleanAssetName;
                           
                           // If we only have asset number, return it
                           if (effort.assetNumber) return effort.assetNumber;
                           
                           return '-';
                         })()}</td>
                        {showAccomplishmentColumn && (
                        <td>
                          {effort.accomplishment != null && effort.accomplishment !== 0
                            ? Number(effort.accomplishment).toFixed(2)
                            : '-'}
                        </td>
                        )}
                        <td>
                          <span 
                            title={effort.comments || ''} 
                            style={{ cursor: effort.comments ? 'help' : 'default' }}
                          >
                            {truncateText(effort.comments)}
                          </span>
                        </td>
                        <td className="tw-text-sm tw-text-content-muted" style={{ whiteSpace: 'pre-line' }}>
                          {effort.type === 'Labour' ? effort.otBreakdown ?? '–' : '–'}
                        </td>
                        <td>{effort.quantity.toFixed(2)}</td>
                        <td>{effort.unitOfMeasure}</td>
                        <td>{effort.rate.toFixed(2)}</td>
                        <td className="tw-text-right">{effort.totalCost.toFixed(2)}</td>
                      </tr>
                    ))}
                    {efforts.length > 0 && (
                      <tr className="tw-bg-surface-alt tw-font-bold">
                        <td colSpan={showAccomplishmentColumn ? 7 : 6}>{type} Total</td>
                        <td>–</td>
                        <td>{totalsByType[type].quantity.toFixed(2)}</td>
                        <td>{totalsByType[type].unitOfMeasure}</td>
                        <td></td>
                        <td className="tw-text-right">${totalsByType[type].cost.toFixed(2)}</td>
                      </tr>
                    )}
                    {efforts.length > pageSize && (
                      <tr>
                        <td colSpan={summaryTableColCount} className="tw-text-center tw-border-0">
                          <ServerPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={efforts.length}
                            pageSize={pageSize}
                            onPageChange={setCurrentPage}
                            onPageSizeChange={handlePageSizeChange}
                            maxVisiblePages={5}
                            className="tw-mb-0 tw-justify-center"
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
                  })
              )}
              {Object.keys(groupedEfforts).length > 0 && (
                <tr className="tw-bg-surface-hover tw-font-bold">
                  <td colSpan={showAccomplishmentColumn ? 11 : 10}>Grand Total</td>
                  <td className="tw-text-right">${grandTotal.toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        )}
      </SectionCard>
    </div>
  );
};


export default FormSummaryView;